import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  motion,
  AnimatePresence, // Import AnimatePresence
} from "framer-motion";
import {
  ArrowLeft,
  Cpu,
  Activity,
  AlertTriangle,
  Wrench,
  FileText,
  MessageSquare,
  TrendingUp,
  Settings,
  ChevronRight,
  Clock,
  Thermometer,
  Gauge,
  Droplets,
  Zap,
  BookOpen,
  ClipboardList,
  Loader2,
} from "lucide-react";
import { machinesAPI } from "../api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import clsx from "clsx";
import MachineConfigModal from "../components/MachineConfigModal";
import HasPermission from "../components/HasPermission";
import { PERMISSIONS } from "../constants/roles";

// ... (statusConfig and sensorIcons remain the same)

const statusConfig = {
  operational: {
    label: "Operational",
    badge: "bg-green-100 text-green-700 border-green-300",
    color: "bg-green-500",
  },
  maintenance: {
    label: "In Maintenance",
    badge: "bg-yellow-100 text-yellow-700 border-yellow-300",
    color: "bg-yellow-500",
  },
  error: {
    label: "Error",
    badge: "bg-red-100 text-red-700 border-red-300",
    color: "bg-red-500",
  },
  offline: {
    label: "Offline",
    badge: "bg-slate-100 text-slate-700 border-slate-300",
    color: "bg-slate-500",
  },
};

const sensorIcons = {
  temperature: Thermometer,
  pressure: Gauge,
  level: Droplets,
  power: Zap,
  vibration: Activity,
};

// Keeping SensorCard as is (it handles its own initial animation)
const SensorCard = ({ sensor }) => {
  const Icon = sensorIcons[sensor.sensor_type] || Activity;
  const isNormal =
    sensor.current_value >= (sensor.min_threshold || 0) &&
    sensor.current_value <= (sensor.max_threshold || 999999);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={clsx(
        "p-5 rounded-2xl border transition-all duration-300 shadow-md h-full flex flex-col",
        isNormal
          ? "bg-white border-slate-200 hover:shadow-xl"
          : "bg-red-50 border-red-300 ring-4 ring-red-100 hover:shadow-xl",
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              "p-2 rounded-xl",
              isNormal ? "bg-blue-50 text-blue-600" : "bg-red-200 text-red-600",
            )}
          >
            <Icon className="w-6 h-6" />
          </div>
          <span className="text-lg font-semibold text-slate-700">
            {sensor.sensor_name}
          </span>
        </div>
        <div
          className={clsx(
            "w-3 h-3 rounded-full",
            isNormal ? "bg-green-500" : "bg-red-500 animate-pulse",
          )}
          title={isNormal ? "Normal" : "Alert"}
        />
      </div>
      <p
        className={clsx(
          "text-2xl font-bold mt-2 leading-tight",
          isNormal ? "text-slate-900" : "text-red-600",
        )}
      >
        {sensor.current_value !== null && sensor.current_value !== undefined
          ? Number(sensor.current_value).toFixed(1)
          : "--"}
        <span className="text-lg font-normal text-slate-400 ml-2">
          {sensor.unit}
        </span>
      </p>
      <div className="mt-auto flex justify-between items-center text-xs text-slate-500 border-t border-slate-100 pt-3">
        <span>Range:</span>
        <span className="font-semibold text-slate-700">
          {sensor.min_threshold} - {sensor.max_threshold} {sensor.unit}
        </span>
      </div>
    </motion.div>
  );
};

// Define transition variants for the main tab content
const contentVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 30, duration: 0.3 },
  },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

const MachineDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

  const { data: machine, isLoading } = useQuery({
    queryKey: ["machine", id],
    queryFn: () => machinesAPI.getById(id).then((res) => res.data.data),
  });

  const { data: healthData } = useQuery({
    queryKey: ["machine-health", id],
    queryFn: () => machinesAPI.getHealth(id).then((res) => res.data.data),
    refetchInterval: 30000,
  });

  // Mock Data
  const sensorHistory = [
    { time: "00:00", temp: 45, pressure: 5.2 },
    { time: "04:00", temp: 48, pressure: 5.4 },
    { time: "08:00", temp: 52, pressure: 5.6 },
    { time: "12:00", temp: 55, pressure: 5.8 },
    { time: "16:00", temp: 51, pressure: 5.5 },
    { time: "20:00", temp: 47, pressure: 5.3 },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-800">
          Machine not found
        </h2>
        <Link
          to="/machines"
          className="text-blue-600 hover:text-blue-700 mt-4 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back to machines
        </Link>
      </div>
    );
  }

  const status = statusConfig[machine.status] || statusConfig.offline;

  // StatCard component remains the same
  const StatCard = ({ title, value, icon: Icon, colorKey }) => {
    const colorClasses = {
      blue: { iconBg: "bg-blue-100 text-blue-600", text: "text-blue-600" },
      red: { iconBg: "bg-red-100 text-red-600", text: "text-red-600" },
      green: { iconBg: "bg-green-100 text-green-600", text: "text-green-600" },
      cyan: { iconBg: "bg-cyan-100 text-cyan-600", text: "text-cyan-600" },
    };

    const colors = colorClasses[colorKey] || colorClasses.blue;

    return (
      <div className="card bg-white border border-slate-200 p-6 shadow-md rounded-2xl flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
        <div className={clsx("p-3 rounded-xl", colors.iconBg)}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    );
  };

  // TabButton component remains the same
  const TabButton = ({ tab, label, icon: Icon }) => {
    const isActive = activeTab === tab;

    return (
      <button
        key={tab}
        onClick={() => setActiveTab(tab)}
        className={clsx(
          "px-5 py-2 text-sm font-semibold capitalize transition-all duration-200 flex items-center gap-2 rounded-full",
          isActive
            ? "bg-blue-600 text-white shadow-md"
            : "text-slate-700 hover:bg-slate-50",
        )}
      >
        <Icon className="w-4 h-4" />
        {label}
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      {/* ... Header and Stats section remain the same ... */}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-200 pb-4">
        <div className="flex items-center gap-4">
          <Link
            to="/machines"
            className="p-3 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">
                {machine.name}
              </h1>
              <span
                className={clsx(
                  "px-3 py-1 text-xs font-semibold rounded-full border",
                  status.badge,
                )}
              >
                <div
                  className={clsx(
                    "w-2 h-2 rounded-full mr-1 inline-block",
                    status.color,
                  )}
                />
                {status.label}
              </span>
            </div>
            <p className="text-md text-slate-500 mt-1">
              {machine.model} • {machine.manufacturer}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 self-end sm:self-start">
          <Link to={`/assistant?machineId=${id}`}>
            <button className="btn btn-secondary gap-2">
              <MessageSquare className="w-4 h-4" />
              Ask AI Assistant
            </button>
          </Link>
          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
            <button
              className="btn btn-primary gap-2"
              onClick={() => setIsConfigModalOpen(true)}
            >
              <Settings className="w-4 h-4" />
              Configure
            </button>
          </HasPermission>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Health Score"
          value={`${healthData?.healthScore?.toFixed(0) || "--"}%`}
          icon={Activity}
          colorKey="blue"
        />
        <StatCard
          title="Active Alerts"
          value={
            machine.recentAlerts?.filter((a) => !a.is_resolved).length || 0
          }
          icon={AlertTriangle}
          colorKey="red"
        />
        <StatCard
          title="Last Maintenance"
          value={
            machine.last_maintenance_date
              ? new Date(machine.last_maintenance_date).toLocaleDateString()
              : "Never"
          }
          icon={Wrench}
          colorKey="green"
        />
        <StatCard
          title="Next Maintenance"
          value={
            machine.next_maintenance_date
              ? new Date(machine.next_maintenance_date).toLocaleDateString()
              : "Not scheduled"
          }
          icon={Clock}
          colorKey="cyan"
        />
      </div>

      {/* Tabs */}
      <div className="p-1 bg-white rounded-full shadow-md border border-slate-100 max-w-fit mx-auto sm:mx-0">
        <div className="flex gap-1 overflow-x-auto">
          <TabButton tab="overview" label="Overview" icon={Cpu} />
          <TabButton tab="sensors" label="Sensors" icon={TrendingUp} />
          <TabButton tab="maintenance" label="Maintenance" icon={Wrench} />
          <TabButton tab="alerts" label="Alerts" icon={AlertTriangle} />
          <TabButton tab="sops" label="SOPs" icon={FileText} />
        </div>
      </div>

      {/* Tab Content Wrapper with AnimatePresence */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab} // Crucial: key changes when the tab changes
          className="pt-4"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={contentVariants}
        >
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Specs Card - removed inner motion.div since parent AnimatePresence handles transition */}
              <div className="card bg-white border border-slate-200 p-6 rounded-2xl shadow-lg h-fit">
                <h3 className="text-xl font-bold text-slate-800 mb-5 border-b border-slate-100 pb-3 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-500" /> Specifications
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      label: "Serial Number",
                      value: machine.serial_number || "N/A",
                      mono: true,
                    },
                    {
                      label: "Location",
                      value: machine.location_in_plant || "N/A",
                    },
                    {
                      label: "Installation Date",
                      value: machine.installation_date
                        ? new Date(
                            machine.installation_date,
                          ).toLocaleDateString()
                        : "N/A",
                    },
                    { label: "Plant", value: machine.plant_name || "N/A" },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-3 -m-3 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      <p className="text-sm text-slate-500">{item.label}</p>
                      <p
                        className={clsx(
                          "text-slate-700 text-sm font-semibold",
                          item.mono && "font-mono",
                        )}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sensor Trends Chart - removed inner motion.div */}
              <div className="card bg-white border border-slate-200 p-6 lg:col-span-2 rounded-2xl shadow-lg">
                <h3 className="text-xl font-bold text-slate-800 mb-5 border-b border-slate-100 pb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" /> Sensor Trends
                  (Last 24 Hours)
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={sensorHistory}
                      margin={{ top: 10, right: 10, bottom: 0, left: -20 }}
                    >
                      <XAxis
                        dataKey="time"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          fontSize: 14,
                          padding: 10,
                          boxShadow:
                            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                        }}
                        labelStyle={{ fontWeight: "bold", color: "#1e293b" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="temp"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        dot={false}
                        name="Temperature (°C)"
                      />
                      <Line
                        type="monotone"
                        dataKey="pressure"
                        stroke="#10b981"
                        strokeWidth={3}
                        dot={false}
                        name="Pressure (Bar)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {activeTab === "sensors" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {machine.sensors && machine.sensors.length > 0 ? (
                // SensorCard uses its own motion properties for staggered effect
                machine.sensors.map((sensor) => (
                  <SensorCard key={sensor.id} sensor={sensor} />
                ))
              ) : (
                <div className="col-span-full card p-12 text-center bg-white border border-slate-200 rounded-2xl shadow-lg">
                  <Activity className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    No sensors configured
                  </h3>
                  <p className="text-slate-500">
                    Use the 'Configure' button to add sensors for real-time
                    monitoring.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "maintenance" && (
            <div className="space-y-4">
              {machine.recentMaintenance &&
              machine.recentMaintenance.length > 0 ? (
                machine.recentMaintenance.map((log, index) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="card bg-white border border-slate-200 p-5 flex items-center justify-between rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Wrench className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-lg">
                          {log.title}
                        </p>
                        <p className="text-sm text-slate-500">
                          {new Date(log.created_at).toLocaleDateString()} •{" "}
                          <span className="capitalize">
                            {log.maintenance_type}
                          </span>
                        </p>
                      </div>
                    </div>
                    <span
                      className={clsx(
                        "px-3 py-1 text-xs font-semibold rounded-full border capitalize",
                        log.status === "completed"
                          ? "bg-green-100 text-green-700 border-green-300"
                          : "bg-yellow-100 text-yellow-700 border-yellow-300",
                      )}
                    >
                      {log.status}
                    </span>
                  </motion.div>
                ))
              ) : (
                <div className="card p-12 text-center bg-white border border-slate-200 rounded-2xl shadow-lg">
                  <ClipboardList className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    No maintenance records
                  </h3>
                  <p className="text-slate-500">
                    Completed work logs will be automatically filed here.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "alerts" && (
            <div className="space-y-4">
              {machine.recentAlerts && machine.recentAlerts.length > 0 ? (
                machine.recentAlerts.map((alert, index) => (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={clsx(
                      "card bg-white p-5 border-l-4 rounded-2xl shadow-sm",
                      alert.severity === "critical"
                        ? "border-l-red-500 bg-red-50"
                        : alert.severity === "high"
                          ? "border-l-yellow-500 bg-yellow-50"
                          : "border-l-blue-500 bg-blue-50",
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-lg text-slate-800">
                          {alert.title}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          {alert.message}
                        </p>
                        <p className="text-xs text-slate-400 mt-3">
                          {new Date(alert.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span
                        className={clsx(
                          "px-3 py-1 text-xs font-semibold rounded-full border",
                          alert.is_resolved
                            ? "bg-green-100 text-green-700 border-green-300"
                            : "bg-red-100 text-red-700 border-red-300",
                        )}
                      >
                        {alert.is_resolved ? "Resolved" : "Active"}
                      </span>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="card p-12 text-center bg-white border border-slate-200 rounded-2xl shadow-lg">
                  <AlertTriangle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    All Clear
                  </h3>
                  <p className="text-slate-500">
                    This machine has no recent or active alerts.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "sops" && (
            <div className="card p-12 text-center bg-white border border-slate-200 rounded-2xl shadow-lg">
              <BookOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-slate-800 mb-4">
                Standard Operating Procedures
              </h3>
              <p className="text-slate-500 mb-6 max-w-lg mx-auto">
                Access the approved procedures and quickly start execution for
                maintenance, operations, or troubleshooting tasks related to
                this machine.
              </p>
              <Link
                to={`/sops?machineId=${id}`}
                className="btn btn-primary gap-2"
              >
                <FileText className="w-4 h-4" /> View Associated SOPs
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <MachineConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        machine={machine}
      />
    </div>
  );
};

export default MachineDetail;
